import { convertPrice } from '@/utils/common';
import BaseModel from './Base.model';
import { PRODUCT_TYPE_ENUM_NAME } from '../../utils/enum-name';
import { PRODUCT_TYPE_ENUM } from '../../utils/enum';

/**
 * Đại diện cho một sản phẩm
 */
export class ProductModel extends BaseModel {
  /**
   * Tạo một instance Product mới
   * @param {Object} data - Đối tượng dữ liệu sản phẩm
   * @param {string} [data.id] - ID sản phẩm (UUID)
   * @param {string} [data.code=''] - Mã sản phẩm
   * @param {string} [data.name=''] - Tên sản phẩm
   * @param {string} [data.type='DEFAULT'] - Loại sản phẩm (enum PRODUCT_TYPE)
   * @param {string} [data.categoryId] - ID nhóm sản phẩm
   * @param {string} [data.parentId] - ID sản phẩm cha
   * @param {number} [data.importPrice=0] - Giá nhập
   * @param {number} [data.sellingPrice=0] - Giá bán
   * @param {string} [data.uomId] - ID đơn vị tính
   * @param {string} [data.brandId] - ID thương hiệu
   * @param {string} [data.version] - Phiên bản xe
   * @param {string} [data.vatType='ZERO_PERCENT'] - Thuế suất (enum VAT_TYPE)
   * @param {string} [data.colorExterior] - Màu sắc ngoại thất
   * @param {string} [data.colorInterior] - Màu sắc nội thất
   * @param {string} [data.companyId] - ID công ty
   * @param {string} [data.productSegmentId1] - ID phân khúc sản phẩm 1
   * @param {string} [data.productSegmentId2] - ID phân khúc sản phẩm 2
   * @param {string} [data.productSegmentId3] - ID phân khúc sản phẩm 3
   * @param {string} [data.productSegmentId4] - ID phân khúc sản phẩm 4
   * @param {string} [data.createdBy] - ID người tạo
   * @param {string} [data.updatedBy] - ID người cập nhật
   * @param {string} [data.description] - Mô tả sản phẩm
   * @param {boolean} [data.deleted=false] - Trạng thái xóa
   * @param {Date|string} [data.createdAt] - Thời gian tạo
   * @param {Date|string} [data.updatedAt] - Thời gian cập nhật
   * @param {Object} [data.uom] - Đơn vị tính
   * @param {Array} [data.uomMulti=[]] - Đơn vị tính đa cấp
   * @param {Object} [data.category] - Danh mục sản phẩm
   * @param {Object} [data.brand] - Thương hiệu
   * @param {Object} [data.parent] - Sản phẩm cha
   * @param {Array} [data.children=[]] - Sản phẩm con
   * @param {Array} [data.stockSlipDetail=[]] - Chi tiết phiếu kho
   * @param {Array} [data.stockSlipTransferDetail=[]] - Chi tiết phiếu chuyển kho
   * @param {Object} [data.productSeries] - Series sản phẩm
   * @param {Object} [data.productChassis] - Chassis sản phẩm
   * @param {Array} [data.productStock=[]] - Tồn kho sản phẩm
   * @param {Array} [data.billItems=[]] - Các item trong hóa đơn
   * @param {Array} [data.orderItems=[]] - Các item trong đơn hàng
   */
  constructor(data = {}) {
    super({
      id: data.id || null,
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null,
    });

    // Basic product information
    this.code = data.code || '';
    this.name = data.name || '';
    this.type = data?.type || PRODUCT_TYPE_ENUM.DEFAULT;
    this.description = data.description || '';
    this.deleted = data.deleted || false;

    // Category and hierarchy
    this.categoryId = data.categoryId || null;
    this.parentId = data.parentId || null;

    // Pricing information
    this.importPrice = data.importPrice || 0;
    this.sellingPrice = data.sellingPrice || 0;
    this.vatType = data.vatType || 'ZERO_PERCENT';

    // Unit of measurement
    this.uomId = data.uomId || null;

    // Brand information
    this.brandId = data.brandId || null;

    // Vehicle specific information
    this.version = data.version || null;
    this.colorExterior = data.colorExterior || null;
    this.colorInterior = data.colorInterior || null;

    // Company and segments
    this.companyId = data.companyId || null;
    this.productSegmentId1 = data.productSegmentId1 || null;
    this.productSegmentId2 = data.productSegmentId2 || null;
    this.productSegmentId3 = data.productSegmentId3 || null;
    this.productSegmentId4 = data.productSegmentId4 || null;

    // Audit fields
    this.createdBy = data.createdBy || null;
    this.updatedBy = data.updatedBy || null;

    // Fomatted name
    this.typeName = this.getProductTypeName();

    // Related entities
    this.uom = data.uom || null;
    this.uomMulti = data.uomMulti || [];
    this.category = data.category || null;
    this.brand = data.brand || null;
    this.parent = data.parent || null;
    this.children = data.children || [];
    this.stockSlipDetail = data.stockSlipDetail || [];
    this.stockSlipTransferDetail = data.stockSlipTransferDetail || [];
    this.productSeries = data.productSeries || null;
    this.productChassis = data.productChassis || null;
    this.productStock = data.productStock || [];
    this.billItems = data.billItems || [];
    this.orderItems = data.orderItems || [];

    // Related fields
    this.quantityInStock = this.calcTotalQuantityInStock();
  }

  /**
   * Get formatted selling price
   * @returns {string}
   */
  getFormattedSellingPrice() {
    return convertPrice(this.sellingPrice);
  }

  /**
   * Get formatted return selling price
   * @returns {string}
   */
  getFormattedReturnPrice() {
    return convertPrice(this.returnSellingPrice);
  }

  /**
   * Get formatted listed price
   * @returns {string}
   */
  getFormattedListedPrice() {
    return convertPrice(this.listedPrice);
  }

  /**
   * Get formatted import price
   * @returns {string}
   */
  getFormattedImportPrice() {
    return convertPrice(this.importPrice);
  }

  /**
   * Check if product is in stock
   * @returns {boolean}
   */
  isInStock() {
    return this.quantityInStock > 0;
  }

  /**
   * Get product type name
   * @returns {string}
   */
  getProductTypeName() {
    return PRODUCT_TYPE_ENUM_NAME[this?.type] || 'N/A';
  }

  /**
   * Get stock status
   * @returns {Object}
   */
  getStockStatus() {
    if (!this.isInStock()) {
      return {
        status: 'out-of-stock',
        label: 'Hết hàng',
        variant: 'danger',
      };
    }

    if (this.isLowStock()) {
      return {
        status: 'low-stock',
        label: 'Sắp hết',
        variant: 'warning',
      };
    }

    if (this.isOverStock()) {
      return {
        status: 'over-stock',
        label: 'Tồn nhiều',
        variant: 'info',
      };
    }

    return {
      status: 'in-stock',
      label: 'Còn hàng',
      variant: 'success',
    };
  }

  calcTotalQuantityInStock() {
    if (!this.productStock || !Array.isArray(this.productStock)) {
      return 0;
    }

    return this.productStock.reduce((total, stock) => {
      return total + (stock.inStockQuantity || 0);
    }, 0);
  }

  /**
   * Check if product can be sold
   * @returns {boolean}
   */
  canBeSold() {
    return this.isActive && (this.isInStock() || this.type === 4);
  }

  /**
   * Create from API response
   * @param {Object} data - API response data
   * @returns {ProductModel}
   */
  static fromJson(data) {
    return BaseModel.fromJson(data, ProductModel);
  }

  /**
   * Create array from API response
   * @param {Array} dataArray - API response data array
   * @returns {ProductModel[]}
   */
  static fromJsonArray(dataArray) {
    return BaseModel.fromJsonArray(dataArray, ProductModel);
  }

  /**
   * Validate product data
   * @returns {Object}
   */
  validate() {
    const errors = [];

    if (!this.productName.trim()) {
      errors.push('Tên sản phẩm không được để trống');
    }

    if (!this.productCode.trim()) {
      errors.push('Mã sản phẩm không được để trống');
    }

    if (this.sellingPrice < 0) {
      errors.push('Giá bán không được âm');
    }

    if (this.importPrice < 0) {
      errors.push('Giá nhập không được âm');
    }
  }

  toJSON() {
    return {
      id: this.id,
      code: this.code,
      name: this.name,
      type: this.type,
      categoryId: this.categoryId,
      parentId: this.parentId,
      importPrice: this.importPrice,
      sellingPrice: this.sellingPrice,
      uomId: this.uomId,
      brandId: this.brandId,
      version: this.version,
      vatType: this.vatType,
      colorExterior: this.colorExterior,
      colorInterior: this.colorInterior,
      companyId: this.companyId,
      productSegmentId1: this.productSegmentId1,
      productSegmentId2: this.productSegmentId2,
      productSegmentId3: this.productSegmentId3,
      productSegmentId4: this.productSegmentId4,
      description: this.description,
      deleted: this.deleted,
    };
  }
}
